import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Loader2, Copy, ExternalLink, ShieldAlert, ShieldCheck, Printer } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  AccreditationTerm,
  TERM_BADGE_CLASSES,
  TERM_LABELS,
} from "@/components/Accreditation/types";
import { PassPrintView } from "@/components/Accreditation/PassPrintView";

const STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-300",
  inactive: "bg-gray-100 text-gray-600 border-gray-300",
  blacklisted: "bg-red-100 text-red-800 border-red-300",
};

const REQUEST_STATUS_BADGE: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  docs_requested: "bg-blue-100 text-blue-800 border-blue-300",
  under_review: "bg-[#0D2240] text-white border-transparent",
  approved: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};

const PASS_STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-300",
  expired: "bg-gray-100 text-gray-600 border-gray-300",
  revoked: "bg-red-100 text-red-800 border-red-300",
};

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

interface JournalistProfileSheetProps {
  journalistId: string | null;
  orgId: string;
  onClose: () => void;
}

export function JournalistProfileSheet({
  journalistId,
  orgId,
  onClose,
}: JournalistProfileSheetProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [printPass, setPrintPass] = useState<any | null>(null);

  const { data: org } = useQuery({
    queryKey: ["journalist_profile_sheet_org", orgId],
    queryFn: async () => {
      const { data } = await supabase
        .from("organizations" as any)
        .select("name, logo_url, brand_primary, brand_accent")
        .eq("id", orgId)
        .maybeSingle();
      return data as any;
    },
    enabled: !!orgId,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["journalist_profile_sheet", journalistId],
    queryFn: async () => {
      const [journalistRes, affiliationRes, requestsRes, passesRes, engagementsRes] =
        await Promise.all([
          supabase.from("journalists" as any).select("*").eq("id", journalistId).single(),
          supabase
            .from("journalist_affiliations" as any)
            .select("outlet_id, role_title, is_primary, media_outlets(name, country, city)")
            .eq("journalist_id", journalistId),
          supabase
            .from("accreditation_requests" as any)
            .select("*")
            .eq("journalist_id", journalistId)
            .order("created_at", { ascending: false }),
          supabase
            .from("press_passes" as any)
            .select("*")
            .eq("journalist_id", journalistId)
            .order("issued_at", { ascending: false }),
          supabase
            .from("journalist_engagements" as any)
            .select("*")
            .eq("journalist_id", journalistId)
            .order("occurred_on", { ascending: false })
            .limit(20),
        ]);

      let submissions: any[] = [];
      let portalProfile: any = null;
      const journalist = journalistRes.data as any;
      if (journalist?.email) {
        const { data: profileMatch } = await supabase
          .from("journalist_profiles" as any)
          .select("id, outlet_name, outlet_logo_url, outlet_verified, outlet_verification_requested_at")
          .eq("email", journalist.email)
          .maybeSingle();
        portalProfile = profileMatch ?? null;
        if ((profileMatch as any)?.id) {
          const { data: subs } = await supabase
            .from("journalist_submissions" as any)
            .select("*")
            .eq("journalist_id", (profileMatch as any).id)
            .order("submitted_at", { ascending: false })
            .limit(50);
          submissions = subs ?? [];
        }
      }

      return {
        journalist,
        portalProfile,
        affiliations: (affiliationRes.data as any[]) ?? [],
        requests: (requestsRes.data as any[]) ?? [],
        passes: (passesRes.data as any[]) ?? [],
        engagements: (engagementsRes.data as any[]) ?? [],
        submissions,
      };
    },
    enabled: !!journalistId,
  });

  const requestReverify = useMutation({
    mutationFn: async () => {
      if (!data?.portalProfile?.id) throw new Error("This journalist has no linked portal account yet");
      const { error } = await (supabase
        .from("journalist_profiles" as any)
        .update({
          outlet_verified: false,
          outlet_verification_requested_by: orgId,
          outlet_verification_requested_at: new Date().toISOString(),
        })
        .eq("id", data.portalProfile.id) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Re-verification requested", description: "The journalist will be prompted to confirm their outlet." });
      qc.invalidateQueries({ queryKey: ["journalist_profile_sheet", journalistId] });
    },
    onError: (err: any) => toast({ title: "Failed to request re-verification", description: err.message, variant: "destructive" }),
  });

  function copyProfileLink() {
    if (!journalistId) return;
    const url = `${window.location.origin}/media-registry?journalist=${journalistId}`;
    navigator.clipboard.writeText(url);
    toast({ title: "Profile link copied" });
  }

  const primaryAffiliation = data?.affiliations.find((a) => a.is_primary) ?? data?.affiliations[0];
  const latestPass = data?.passes[0];
  const currentTerm: AccreditationTerm | undefined =
    latestPass?.accreditation_term ?? data?.requests[0]?.accreditation_term;

  return (
    <Sheet open={!!journalistId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        {isLoading || !data?.journalist ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <SheetHeader className="mb-6">
              <SheetTitle className="sr-only">Journalist profile</SheetTitle>
              <div className="flex items-start gap-4">
                {data.journalist.photo_url ? (
                  <img
                    src={data.journalist.photo_url}
                    alt=""
                    className="w-20 h-20 rounded-lg object-cover shrink-0 border border-border"
                  />
                ) : org?.logo_url ? (
                  <img
                    src={org.logo_url}
                    alt=""
                    className="w-20 h-20 rounded-lg object-contain shrink-0 border border-border bg-muted p-2"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center shrink-0 text-lg font-semibold text-muted-foreground border border-border">
                    {data.journalist.first_name?.[0]}
                    {data.journalist.last_name?.[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-semibold">
                      {data.journalist.first_name} {data.journalist.last_name}
                    </h2>
                    <Badge
                      variant="outline"
                      className={STATUS_BADGE[data.journalist.status] ?? ""}
                    >
                      {data.journalist.status}
                    </Badge>
                    {currentTerm && (
                      <Badge variant="outline" className={TERM_BADGE_CLASSES[currentTerm]}>
                        {TERM_LABELS[currentTerm]}
                      </Badge>
                    )}
                  </div>
                  <p className="text-justify text-sm text-muted-foreground mt-0.5">
                    {primaryAffiliation?.media_outlets?.name ?? "No outlet on file"}
                    {primaryAffiliation?.role_title ? ` · ${primaryAffiliation.role_title}` : ""}
                  </p>
                  <p className="text-justify text-xs text-muted-foreground mt-1">
                    {data.journalist.nationality ?? "—"} · Verification ID{" "}
                    <span className="font-mono">
                      {data.journalist.verification_id ?? data.journalist.id.slice(0, 8)}
                    </span>
                  </p>
                  <button
                    onClick={copyProfileLink}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-2"
                  >
                    <Copy className="w-3 h-3" /> Copy profile link
                  </button>
                </div>
              </div>
            </SheetHeader>

            {/* Overview */}
            <Section title="Overview">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <Field label="Email" value={data.journalist.email} />
                <Field label="Phone" value={data.journalist.phone} />
                <Field label="WhatsApp" value={data.journalist.whatsapp} />
                <Field label="Gender" value={data.journalist.gender} />
                <Field label="Seniority" value={data.journalist.seniority?.replace(/_/g, " ")} />
                <Field
                  label="Languages"
                  value={(data.journalist.languages ?? []).join(", ") || null}
                />
                <Field
                  label="Beats"
                  value={(data.journalist.beats ?? []).join(", ") || null}
                />
                <Field label="Consent to marketing" value={data.journalist.consent_marketing ? "Yes" : "No"} />
                <Field label="Position / Designation" value={data.journalist.position} />
                <Field label="Verification ID" value={data.journalist.verification_id ?? data.journalist.id.slice(0, 8)} />
                <Field label="Press card status" value={data.journalist.press_card_status} />
                <Field label="Next of kin" value={data.journalist.next_of_kin} />
                <Field label="Emergency contact" value={data.journalist.emergency_contact} />
              </div>
              {data.journalist.bio && (
                <p className="text-justify text-sm text-muted-foreground mt-3">{data.journalist.bio}</p>
              )}
              {data.journalist.notes && (
                <p className="text-justify text-sm text-muted-foreground mt-2 italic">
                  Notes: {data.journalist.notes}
                </p>
              )}
              {data.journalist.status === "blacklisted" && data.journalist.blacklist_reason && (
                <p className="text-justify text-sm text-destructive mt-2">
                  Blacklist reason: {data.journalist.blacklist_reason}
                </p>
              )}

              {/* Outlet declared by the journalist on their portal profile */}
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-justify text-xs font-medium text-muted-foreground mb-2">Declared outlet</p>
                {data.portalProfile ? (
                  <div className="flex items-center gap-3">
                    {data.portalProfile.outlet_logo_url ? (
                      <img
                        src={data.portalProfile.outlet_logo_url}
                        alt=""
                        className="w-9 h-9 rounded-md object-contain bg-card border border-border shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-md bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">
                        {data.portalProfile.outlet_name?.[0] ?? "?"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {data.portalProfile.outlet_name ?? "Not declared yet"}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        {data.portalProfile.outlet_verified ? (
                          <Badge variant="outline" className="text-[10px] gap-1 bg-success/15 text-success border-success/30">
                            <ShieldCheck className="w-3 h-3" /> Verified
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] gap-1 bg-warning/15 text-warning border-warning/30">
                            <ShieldAlert className="w-3 h-3" />
                            {data.portalProfile.outlet_verification_requested_at ? "Re-verification requested" : "Unverified"}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={requestReverify.isPending || !data.portalProfile.outlet_verified && !!data.portalProfile.outlet_verification_requested_at}
                      onClick={() => requestReverify.mutate()}
                    >
                      {requestReverify.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                      Request re-verification
                    </Button>
                  </div>
                ) : (
                  <p className="text-justify text-sm text-muted-foreground">
                    This journalist has no linked portal account, so outlet re-verification is unavailable.
                  </p>
                )}
              </div>
            </Section>

            {/* Accreditation */}
            <Section title="Accreditation">
              <p className="text-justify text-xs font-medium text-muted-foreground mb-2">Request history</p>
              <div className="space-y-2 mb-4">
                {data.requests.length === 0 && (
                  <p className="text-justify text-sm text-muted-foreground">No requests on file.</p>
                )}
                {data.requests.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-md border p-2.5 text-sm">
                    <div>
                      <div>{fmtDate(r.created_at)}</div>
                      {r.rejection_reason && (
                        <div className="text-xs text-muted-foreground">{r.rejection_reason}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {r.accreditation_term && (
                        <Badge variant="outline" className={TERM_BADGE_CLASSES[r.accreditation_term as AccreditationTerm]}>
                          {TERM_LABELS[r.accreditation_term as AccreditationTerm]}
                        </Badge>
                      )}
                      <Badge variant="outline" className={REQUEST_STATUS_BADGE[r.status] ?? ""}>
                        {r.status.replace(/_/g, " ")}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-justify text-xs font-medium text-muted-foreground mb-2">Press passes issued</p>
              <div className="space-y-2">
                {data.passes.length === 0 && (
                  <p className="text-justify text-sm text-muted-foreground">No press passes issued.</p>
                )}
                {data.passes.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-md border p-2.5 text-sm">
                    <div>
                      <div className="font-mono font-medium" style={{ color: "#006B3C" }}>
                        {p.pass_number}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {fmtDate(p.valid_from)} → {fmtDate(p.valid_to)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.accreditation_term && (
                        <Badge variant="outline" className={TERM_BADGE_CLASSES[p.accreditation_term as AccreditationTerm]}>
                          {TERM_LABELS[p.accreditation_term as AccreditationTerm]}
                        </Badge>
                      )}
                      <Badge variant="outline" className={PASS_STATUS_BADGE[p.status] ?? ""}>
                        {p.status}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1.5 text-xs"
                        onClick={() => setPrintPass(p)}
                      >
                        <Printer className="w-3.5 h-3.5" /> ID Card
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* Submissions */}
            <Section title="Submissions">
              {data.submissions.length === 0 ? (
                <p className="text-justify text-sm text-muted-foreground">
                  No coverage submissions on file for this journalist.
                </p>
              ) : (
                <div className="space-y-3">
                  {data.submissions.map((s) => (
                    <SubmissionCard key={s.id} submission={s} />
                  ))}
                </div>
              )}
            </Section>

            {/* Engagements */}
            <Section title="Engagements">
              {data.engagements.length === 0 ? (
                <p className="text-justify text-sm text-muted-foreground">No engagements logged.</p>
              ) : (
                <div className="space-y-2">
                  {data.engagements.map((e) => (
                    <div key={e.id} className="flex items-center justify-between rounded-md border p-2.5 text-sm">
                      <div>
                        <div className="capitalize">{e.engagement_type.replace(/_/g, " ")}</div>
                        {e.summary && (
                          <div className="text-xs text-muted-foreground">{e.summary}</div>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{fmtDate(e.occurred_on)}</div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </>
        )}
      </SheetContent>

      {printPass && data?.journalist && (
        <PassPrintView
          pass={{
            ...printPass,
            journalist: {
              first_name: data.journalist.first_name,
              last_name: data.journalist.last_name,
              nationality: data.journalist.nationality,
              photo_url: data.journalist.photo_url,
              verification_id: data.journalist.verification_id,
              position: data.journalist.position,
            },
          }}
          organization={org ?? null}
          canPrint
          onClose={() => setPrintPass(null)}
        />
      )}
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold mb-3">{title}</h3>
      {children}
      <Separator className="mt-6" />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="capitalize">{value}</div>
    </div>
  );
}

function SubmissionCard({ submission }: { submission: any }) {
  return (
    <div className="rounded-md border p-3 text-sm space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{fmtDate(submission.submitted_at)}</span>
        <div className="flex items-center gap-2">
          {submission.is_flagged && (
            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
              Flagged
            </Badge>
          )}
          {submission.similarity_score != null && (
            <Badge variant="secondary">{Math.round(submission.similarity_score * 100)}% similarity</Badge>
          )}
        </div>
      </div>
      {submission.content_url && (
        <a
          href={submission.content_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <ExternalLink className="w-3 h-3" /> {submission.content_url}
        </a>
      )}
      {submission.content_body && (
        <details className="mt-1">
          <summary className="cursor-pointer text-xs text-muted-foreground">
            View full submission text
          </summary>
          <p className="text-justify whitespace-pre-wrap text-sm mt-2">{submission.content_body}</p>
        </details>
      )}
    </div>
  );
}
